/**
 * One connection row — prototype/integrations-frames.js `row()`. Provider
 * mark, identity (+ Beta badge for Outlook, per AvailableIntegrations'
 * `beta` flag), right-cluster actions (auto-sync switch, Resync, kebab), a
 * meta line with absolute timestamps (the dashboard widget uses relative
 * time; this page doesn't), and at most one status strip. The meta line
 * itself carries the healthy/not-synced distinction (CheckCircle vs Clock)
 * — those two tiers never get their own strip.
 */
import React, { useCallback } from "react";
import classNames from "classnames";
import Badge from "../../theme/untitled/Badge/Badge";
import Button from "../../theme/untitled/Button/Button";
import Icon from "../../theme/untitled/icons/Icon";
import { AvailableIntegrations } from "../../consts/integrations";
import {
  INTEGRATION_ITEM_NAMES,
  DEFAULT_INTEGRATION_ITEM_NAME,
} from "../../../../consts";
import { getSourceName } from "../DashboardPage/CalendarSourcesWidget/utils";
import { getConnectionStatus } from "./getConnectionStatus";
import StatusStrip from "./StatusStrip";
import AutoSyncSwitch from "./AutoSyncSwitch";
import RowMenu from "./RowMenu";
import styles from "./ConnectionRow.module.scss";

const dateTimeFormatter = new Intl.DateTimeFormat(
  window.navigator.language || "en-US",
  {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  }
);

const ConnectionRow = ({
  integration,
  autoSyncSaving,
  onToggleAutoSync,
  onResync,
  onEditSettings,
  onFullResync,
  onDisconnect,
}) => {
  const meta = AvailableIntegrations.find((a) => a.type === integration.type);
  const status = getConnectionStatus(integration);
  const itemName =
    INTEGRATION_ITEM_NAMES[integration.type] || DEFAULT_INTEGRATION_ITEM_NAME;
  const syncedItemCount = (integration.settings?.calendars || []).filter(
    (c) => c.sync
  ).length;

  const handleToggleAutoSync = useCallback(
    () => onToggleAutoSync(integration),
    [onToggleAutoSync, integration]
  );
  const handleResync = useCallback(
    () => onResync(integration),
    [onResync, integration]
  );

  const metaText = integration.last_sync
    ? `${status.disabled ? "Last synced" : "Synced"} ${dateTimeFormatter.format(
        new Date(integration.last_sync * 1000)
      )}${
        syncedItemCount
          ? ` · ${syncedItemCount} ${itemName}${
              syncedItemCount === 1 ? "" : "s"
            }`
          : ""
      }${status.disabled ? " · kept, not deleted" : ""}`
    : "Not synced yet";

  return (
    <div
      className={classNames(styles.row, {
        [styles["row--dimmed"]]: status.disabled,
      })}
    >
      <div className={styles.top}>
        <span className={styles.mark}>
          {meta ? (
            <img src={`/img/integrations_icons/${meta.icon}`} alt="" />
          ) : null}
        </span>

        <div className={styles.identity}>
          <div className={styles.identityTop}>
            <span className={styles.name}>{getSourceName(integration)}</span>
            {meta?.beta && (
              <Badge color="blueLight" size="sm">
                Beta
              </Badge>
            )}
            {status.disabled && (
              <Badge color="error" size="sm">
                Disabled
              </Badge>
            )}
          </div>
          <span className={styles.account}>{integration.username}</span>
        </div>

        <div className={styles.syncActions}>
          <AutoSyncSwitch
            checked={Boolean(Number(integration.is_auto_sync))}
            saving={autoSyncSaving}
            disabled={status.disabled}
            onChange={handleToggleAutoSync}
          />
          <Button
            hierarchy="secondaryGray"
            size="sm"
            iconLeading={<Icon name="RefreshCcw01" size={16} />}
            disabled={status.kind === "progress" || status.disabled}
            onClick={handleResync}
          >
            Resync
          </Button>
        </div>
        <span className={styles.kebabWrap}>
          <RowMenu
            integration={integration}
            syncing={status.kind === "progress"}
            onEditSettings={onEditSettings}
            onResync={onResync}
            onFullResync={onFullResync}
            onDisconnect={onDisconnect}
          />
        </span>
      </div>

      <div className={styles.indented}>
        {!status.hideMeta && (
          <span
            className={classNames(styles.metaLine, {
              [styles.metaLineOk]: status.metaOk,
            })}
          >
            <Icon
              name={status.metaIcon || "Clock"}
              size={16}
              className={styles.metaIcon}
            />
            {metaText}
          </span>
        )}
        <StatusStrip status={status} integration={integration} />
      </div>
    </div>
  );
};

export default ConnectionRow;
